import {Back, Linear, Power2, TimelineLite, TweenMax} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {RoadKill} from "../../../game/RoadKill";
import {RoadKillSound} from "../../../game/sound/RoadKillSound";
import {RoadKillGameConfig} from "../../../game/RoadKillGameConfig";
import {SimpleReelModelState} from "../../../game/component/enums/RoadKillEnums";
import {RoadKillSymbol} from "../../../game/reels/RoadKillSymbol";

export interface Positionable {
    position: {
        x: number,
        y: number
    }
}

export interface SimpleReelModelSymbolPosition {
    reelID: number;
    position: number
}


export interface SimpleReelModelDelegate {
    createSymbol(symbolName: string, state: SimpleReelModelState, position: SimpleReelModelSymbolPosition): Positionable;

    removeSymbol(symbolData: SimpleReelModelSymbolData, state: SimpleReelModelState): void;
}

export interface SimpleReelModelSymbolData {
    name: string,
    positionable: Positionable,
    reelPosition: number
}

export interface SimpleReelModelEventData {
    reel: number;
    state: SimpleReelModelState;
}


export interface SimpleReelPaddingData {
    visualPaddingBefore: number;
    paddingBefore: number;
    visibleSymbols: number;
    paddingAfter: number;
    visualPaddingAfter: number;
}

export class SimpleReelModel extends PIXI.Container {
    public static SPEED: number = 1;
    public paddingConfig: SimpleReelPaddingData = {
        visualPaddingBefore: 1,
        paddingBefore: 0,
        visibleSymbols: 3,
        paddingAfter: 0,
        visualPaddingAfter: 1,
    }
    public animatedStepData = {
        y: 0
    }
    public stepSize: number = 180;
    public stepDuration = 0.1;
    public readonly id: number;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     *
     *
     */
    public readonly events: APIEventSystem | undefined;
    private _reelSetIndex: number = 0;
    private _stopIndex: number;
    private _spinning: boolean = false;
    private _currentSpinStep: TweenMax | undefined;
    private _stopping: boolean = false;
    private triggerStop: boolean = false;
    private _startedReverse: boolean;
    private masterSpinTimeline: TimelineLite | undefined;
    private _delegate: SimpleReelModelDelegate;
    private _nudgeSteps: number = 0;
    private _stopTriggerTl: TimelineLite | undefined;
    private _stopSymbols: SimpleReelModelSymbolData[] = [];
    private _stopTimeline: TimelineLite | undefined;
    //Fake reel
    private _reelSet: string[] | undefined;
    private _symbols: SimpleReelModelSymbolData[] = [];
    private _state: SimpleReelModelState = SimpleReelModelState.IDLE;

    constructor(id: number, delegate: SimpleReelModelDelegate, triggerEvents: boolean = false) {
        super();
        this.name = `Reel_${id}`
        this.id = id;
        this._delegate = delegate;
        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data: SimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }
    }

    public setReelSet(reelSet: string[], index?: number) {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    public init(symbols: string[]) {
        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);

        const totalInitCount = this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }

        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(symbols[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.IDLE);
            this._symbols.push(symbol);
            this._stopSymbols.push(symbol);
        }

        this.updatePosition();
    }

    public stop(stopSymbolNames: string[], reverse: boolean = false): TimelineLite {
        reverse = this._startedReverse;

        this.clearSymbols(this._stopSymbols);

        this._stopSymbols = [];
        for (let i: number = 0; i < stopSymbolNames.length; i++) {
            const symbol = this.createNewSymbol(stopSymbolNames[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.SPIN);
            symbol.positionable.position.y = this.stepSize * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
            this._stopSymbols.push(symbol);
        }

        this._stopTriggerTl = new TimelineLite({
            paused: true,
            onStart: () => {
                this.state = SimpleReelModelState.STOP;
                this._spinning = false;
                this.triggerStop = true;
            }
        });

        this._stopTimeline = new TimelineLite({
            paused: true,
            onStart: () => {
                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;
                RoadKill.reelView.turnOffGraphicOverlay(this.id);
            },
            onComplete: () => {
                this.state = SimpleReelModelState.STOPPED;
                this._stopTimeline = undefined;
                this._stopping = false;
                this.state = SimpleReelModelState.IDLE;
            }
        });

        const numberOfNormalSteps = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step(reverse);
            this._stopTimeline.add(tween);
        }

        this._stopTimeline.add(this.bounce());

        this._stopTriggerTl.add(new TweenMax(this, this._stopTimeline.totalDuration(), {}));

        return <TimelineLite>this._stopTriggerTl.play(0);
    }

    public start(reverse: boolean = false): TimelineLite {
        console.log("Simple reel model start called ", this.id);
        this._startedReverse = reverse;
        const tl: TimelineLite = new TimelineLite({
            onStart: () => {
                this.state = SimpleReelModelState.START;
                this._stopSymbols = [];
                this._nudgeSteps = 0;
            },
            onComplete: () => {
                this.startSpin(reverse);
                RoadKill.reelView.turnOnGraphicOverlay(this.id);
            }
        });
        if (this.id === 0) {
            tl.add(() => {
                RoadKill.soundPlayer.playTrack(RoadKillSound.REELS_SPINNING, 1, Number.MAX_VALUE);
            })
        }

        const tween = this.step(reverse);
        tween.vars.ease = Back.easeIn.config(3);
        tween.totalDuration(this.stepDuration * SimpleReelModel.SPEED * 8);

        tl.add(tween);

        return tl;
    }

    public bounce(bounceFactor: number = 0.2, durationFactor: number = 1, reverse: boolean = false): TimelineLite {
        const tl = new TimelineLite();
        const overshoot = TweenMax.to(this.animatedStepData, this.stepDuration * SimpleReelModel.SPEED * durationFactor, {
            y: reverse ? -this.stepSize * bounceFactor : this.stepSize * bounceFactor,
            ease: Power2.easeOut,
            onStart: () => {
                this.state = SimpleReelModelState.BOUNCE;
            },
            onUpdate: () => this.updatePosition(),
        });

        const back = TweenMax.to(this.animatedStepData, this.stepDuration * SimpleReelModel.SPEED * durationFactor * 2, {
            y: 0,
            ease: Power2.easeOut,
            onUpdate: () => this.updatePosition(),
        });
        if (this.id === RoadKillGameConfig.REELS_NUM - 1) {
            tl.add(() => {
                RoadKill.soundPlayer.stopTrack(RoadKillSound.REELS_SPINNING);
            })
        }
        tl.add(() => {
            RoadKill.soundPlayer.playTrack(RoadKillSound.REELS_LANDING[this.id]);
        })
        tl.add(overshoot);
        tl.add(back);

        return tl;
    }

    public getAllVisibleStopSymbols(): SimpleReelModelSymbolData [] {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        return this._stopSymbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
    }

    public getAllNotVisibleStopSymbols(): SimpleReelModelSymbolData [] {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        return this._stopSymbols.slice(0, paddingBefore).concat(this._stopSymbols.slice(paddingBefore + this.paddingConfig.visibleSymbols))
    }

    private onReelStateChange(data: SimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    private getNextSymbolName(nextSymbolPosition: number): string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        return this.reelSet[nextIndex];

    }

    private getNextSymbol(position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        if (this._stopping && this._stopSymbols.length > 0 && this._stopSymbols[this._stopIndex]) {
            const symbol = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }
        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position, state)
    }

    private createNewSymbol(name: string, position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        const symbol: SimpleReelModelSymbolData = {
            name: name,
            positionable: this._delegate.createSymbol(name, state, {reelID: this.id, position: position}),
            reelPosition: position
        }
        return symbol;
    }

    private clearSymbols(symbols: SimpleReelModelSymbolData[]): void {
        let symbol;
        while (symbol = symbols.pop()) {
            this._delegate.removeSymbol(symbol, this.state);
        }
    }

    private startSpin(reverse: boolean = false) {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this.masterSpinTimeline = new TimelineLite();
        this.masterSpinTimeline.add(this.spin(reverse));
    }

    private spin(reverse: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                if (this._spinning) {
                    this.masterSpinTimeline?.add(this.spin(reverse));
                    //this.spin(reverse);
                }
            }
        })
        tl.add(this.step(reverse));
        return tl;
    }

    private step(reverse: boolean = false): TweenMax {
        const tween: TweenMax = TweenMax.to(this.animatedStepData, this.stepDuration * SimpleReelModel.SPEED, {
            y: reverse ? -this.stepSize : this.stepSize,
            ease: Linear.easeNone,
            onStart: () => this.onAnimationStart(tween, reverse),
            onUpdate: () => this.updatePosition(),
            onComplete: () => this.onAnimationComplete(reverse)
        });
        return tween;
    }

    private onAnimationStart(tween: TweenMax, reverse: boolean = false) {
        this._currentSpinStep = tween;
        this.addSymbol(reverse);
    }

    private onAnimationComplete(reverse: boolean = false) {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this._stopTimeline?.play(0);
        }

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse: boolean) {
        const valueChange = reverse ? 1 : -1
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number) {
        if (value >= this._stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this._stopSymbols.length - 1;
        }
        this._stopIndex = value;
    }

    private setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(reverse: boolean) {
        let newSymbols = [];
        if (reverse) {
            const newSymbolPosition = this._symbols[this._symbols.length - 1].reelPosition + 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
            // newSymbols.push(this.createNewSymbol(this.getNextSymbolName(newSymbolPosition), newSymbolPosition, this.state));
        } else {
            const newSymbolPosition = this._symbols[0].reelPosition - 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
        }
        this._symbols = this._symbols.concat(newSymbols);
        this._symbols.sort((a: any, b: any) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        })

    }

    private removeSymbol() {
        for (let i: number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition >= this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter) {
                //Remove from bottom
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol, this.state);


            }
            if (symbol.reelPosition < -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore) {
                //Remove from top
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol, this.state);

            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            const basePos = this.stepSize * symbol.reelPosition;
            if ((symbol.positionable as RoadKillSymbol).parent) {
                symbol.positionable.position.y = basePos + this.animatedStepData.y;
            }
        }
    }

    get reelSet(): string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    get symbols(): SimpleReelModelSymbolData[] {
        return this._symbols;
    }

    get state(): SimpleReelModelState {
        return this._state;
    }

    set state(value: SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel: this.id, state: this._state});
        }
    }


}